import { createFileRoute, Navigate } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { useSession } from "../lib/auth";
import { useSuperadminCheck } from "../hooks/useSuperadmin";
import { api } from "../lib/api";

export const Route = createFileRoute("/")({
  component: RootRedirect,
});

function RootRedirect() {
  const { data: session, isPending: isSessionLoading } = useSession();
  const isAuthed = !!session?.user;

  const { data: superadminData } = useSuperadminCheck(isAuthed);
  const isSuperadmin = superadminData?.isSuperadmin ?? false;

  const orgsQuery = useQuery({
    queryKey: ["orgs", "me"],
    queryFn: async () => {
      const res = await api.orgs.me.$get({});
      if (!res.ok) throw new Error("Failed to fetch orgs");
      return res.json() as Promise<{ organizations: { slug: string }[] }>;
    },
    enabled: isAuthed && !isSuperadmin,
  });

  if (isSessionLoading) {
    return (
      <div className="flex h-dvh items-center justify-center">
        <div className="h-8 w-8 animate-spin rounded-full border-4 border-primary border-t-transparent" />
      </div>
    );
  }

  if (!isAuthed) {
    return <Navigate to="/login" search={{ redirect: "/" }} replace />;
  }

  // Superadmins go to superadmin panel
  if (isSuperadmin) {
    return <Navigate to="/superadmin" replace />;
  }

  // Wait for orgs to load
  if (orgsQuery.isLoading) {
    return (
      <div className="flex h-dvh items-center justify-center">
        <div className="h-8 w-8 animate-spin rounded-full border-4 border-primary border-t-transparent" />
      </div>
    );
  }

  // Redirect to first org's admin dashboard
  const firstOrg = orgsQuery.data?.organizations?.[0];
  if (firstOrg) {
    return (
      <Navigate
        to="/$orgSlug/admin"
        params={{ orgSlug: firstOrg.slug }}
        replace
      />
    );
  }

  // No orgs — send to create-org
  return <Navigate to="/create-org" replace />;
}
